import { Component } from '@theme/component';

class KayaHeroVideo extends Component {
  requiredRefs = ['video', 'poster'];
  #controller;
  #intersection;
  #frame;
  #visible = false;
  #compactFailed = false;
  #selectedSource;

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#disconnect();
    this.#connect();
  }

  disconnectedCallback() {
    this.#disconnect();
    super.disconnectedCallback();
  }

  #connect() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { video, poster } = this.refs;
    const compact = window.matchMedia('(max-width: 749px)').matches;
    this.#selectedSource = (compact && video.dataset.compactSrc) || video.dataset.fallbackSrc;
    this.#compactFailed = false;
    video.muted = true;
    video.addEventListener('playing', () => this.setAttribute('data-playing', ''), { signal });
    video.addEventListener('error', () => {
      this.removeAttribute('data-playing');
      if (!this.#compactFailed && video.getAttribute('src') !== video.dataset.fallbackSrc) {
        this.#compactFailed = true;
        this.#resume();
      }
    }, { signal });
    poster.addEventListener('load', () => this.#resume(), { signal });
    poster.addEventListener('error', () => this.#resume(), { signal });
    document.addEventListener('visibilitychange', () => this.#resume(), { signal });
    this.#intersection = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#resume();
    });
    this.#intersection.observe(this);
  }

  #resume() {
    const { video, poster } = this.refs;
    cancelAnimationFrame(this.#frame);
    if (!this.isConnected || !this.#visible || document.hidden) {
      video.pause();
      return;
    }
    if (!poster.complete) return;
    this.#frame = requestAnimationFrame(() => {
      if (!this.isConnected || !this.#visible || document.hidden) return;
      const source = this.#compactFailed ? video.dataset.fallbackSrc : this.#selectedSource;
      if (!source) return;
      if (video.getAttribute('src') !== source) {
        this.removeAttribute('data-playing');
        video.src = source;
        video.load();
      }
      if (video.paused) video.play().catch(() => {});
    });
  }

  #disconnect() {
    this.#controller?.abort();
    this.#intersection?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.refs.video?.pause();
  }
}

if (!customElements.get('kaya-hero-video')) {
  customElements.define('kaya-hero-video', KayaHeroVideo);
}
